import cx from 'classnames'
import { PropsWithChildren, useState, useRef, useEffect, useMemo } from 'react'
import { Modal, useModals, StackModalProps } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'

import Styles from './CreditsBalanceModal.module.scss'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { Button } from 'blaze-ui/Button'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { ChevronDown2, LinkExternal, AlertTriangle, ArrowRefresh2, File02, UserProfileAdd2 } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import dayjs from 'dayjs'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'
import { pluralize } from 'utils'
import { findPlanCreditLimit } from 'components/Settings/Workspace/AutopilotPlans/utils'
import LockOpen3 from 'blaze-ui/icons/24/LockOpen3'
import { Image03, Images, VideoOn, Email } from 'blaze-ui/icons/24'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Text } from 'blaze-ui/Text/Text'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { AutoReloadModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/AutoReloadModal/AutoReloadModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Iphone02 } from 'blaze-ui/icons/16'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'

const EducationRow = ({
  text,
  children,
  isOpen,
  onToggle,
}: PropsWithChildren<{
  text: string
  isOpen: boolean
  onToggle: () => void
}>) => {
  return (
    <div className={Styles.educationRow}>
      <div className={Styles.textAndChevron} onClick={onToggle}>
        <span className={cx(Styles.text, Styles.grow)}>{text}</span>
        <div className={cx(Styles.chevronWrapper, { [Styles.rotated]: isOpen })}>
          <ChevronDown2 />
        </div>
      </div>
      <div className={cx(Styles.accordionContent, { [Styles.open]: isOpen })}>
        <div className={Styles.accordionInner}>{children}</div>
      </div>
    </div>
  )
}

type Props = StackModalProps

export const CreditsBalanceModal = observer((_props: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { openModal, closeModal } = useModals()
  const {
    creditsRemaining,
    baseCreditsAvailable,
    usageCosts,
    endDate,
    creditStatus,
    canAutoReload,
    creditsExpiringSoon,
  } = useCreditUsage()
  const {
    tierUsageStore: { currentSubscription, autopilotAddOns, autopilotPlans },
  } = useRootStore()
  const [openAccordion, setOpenAccordion] = useState<string | null>('what-uses-credits')
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const workspace = useCurrentWorkspace()
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const autoReloadIsOn = canAutoReload
  const autoReloadIsOff = !workspace?.auto_reload_credits
  const autoReloadIsPaused = workspace?.auto_reload_credits && !canAutoReload

  const { data: brandPlan } = useCurrentBrandPlan()
  const hasContentStrategies = !!brandPlan?.has_content_strategies

  const isStarterOrFreePlan =
    currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotStarter ||
    currentSubscription?.plan === PaymentPlanKey.Free ||
    workspace?.is_ap_freemium
  const currentPlan = autopilotPlans.find((plan) => plan.key === currentSubscription?.plan)

  const openPurchaseCreditsModal = () => {
    if (!isOnPioneer && currentSubscription?.status === 'trialing' && currentPlan) {
      openModal(AutopilotPlansModal, {
        initialPlanAndPrice: { plan: currentPlan },
        initialStep: 'selected',
      })
      return
    }

    if (!autopilotContentCreditsPrice) return

    closeModal()
    openModal(PurchaseContentCreditsModal, { price: autopilotContentCreditsPrice })
  }

  const openReferralModal = () => {
    openModal(ReferralModalV2, { onAction: closeModal })
  }

  const toggleAccordion = (accordionKey: string) => {
    // Clear any existing timeout
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }

    // If clicking the same accordion, just toggle it
    if (openAccordion === accordionKey) {
      setOpenAccordion(null)
      return
    }

    // If no accordion is open, open the clicked one immediately
    if (!openAccordion) {
      setOpenAccordion(accordionKey)
      return
    }

    // If switching between accordions, close current one first
    setOpenAccordion(null)

    // Wait for close animation to complete, then open new one
    transitionTimeoutRef.current = setTimeout(() => {
      setOpenAccordion(accordionKey)
      transitionTimeoutRef.current = null
    }, 300) // Match the CSS transition duration
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  const pluralizedCredits = (cost: number) => {
    return `${cost} ${pluralize('credit', cost)}`
  }

  const isTrialing = currentSubscription?.status === 'trialing'
  const planKey =
    currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth
      ? PaymentPlanKey.BlazeAutopilotGrowth
      : PaymentPlanKey.BlazeAutopilotStarter

  // Map credit status from hook to modal variant format
  const variant = useMemo(() => {
    if (creditStatus === 'negative') return 'negative_credits'
    if (creditStatus === 'out') return 'no_credits'
    if (creditStatus === 'low') return 'low_on_credits'
    if (workspace?.auto_reload_credits && !canAutoReload) {
      return 'auto_reload_limit_reached'
    }
    return 'credit_balance'
  }, [creditStatus, canAutoReload, workspace?.auto_reload_credits])

  const planName = planKey === PaymentPlanKey.BlazeAutopilotGrowth ? 'Growth' : 'Starter'
  const ctaButtonLabel =
    currentSubscription?.status === 'active' ? 'Add More Credits' : `Activate Your ${planName} Plan`

  const modalHeader = useMemo(() => {
    if (variant === 'negative_credits') {
      return "You've used more credits than expected"
    }
    if (variant === 'low_on_credits') {
      return "You're low on credits"
    }
    if (variant === 'no_credits') {
      return "You've used all your credits"
    }
    if (variant === 'auto_reload_limit_reached') {
      return "You've reached your auto-reload budget"
    }
    return ''
  }, [variant])

  const modalDescription = useMemo(() => {
    if (isTrialing) {
      return <div>{baseCreditsAvailable} credits included with your trial.</div>
    }
    if (variant === 'negative_credits') {
      return (
        <>
          <p>
            Since content is generated in batches, the exact credit usage can vary. Your last batch required a few more
            credits than expected.
          </p>
          <p>
            Your credits refresh on {dayjs(endDate).add(1, 'day').format('MMM D')}. To continue generating content now,
            add more credits.
          </p>
        </>
      )
    }
    return null
  }, [variant, isTrialing, baseCreditsAvailable, endDate])

  const openUpgradeToGrowthModal = () => {
    openModal(AutopilotUpgradeEntryModal, {
      variant: 'more_credits',
      targetPlanKey: PaymentPlanKey.BlazeAutopilotGrowth,
    })
  }

  return (
    <Modal.Root bodyClassName={cx({ [Styles.modalContainer]: !isMobile })}>
      <Modal.Header title="Your credits balance" />
      <Modal.Content withoutFooter className={isMobile ? Styles.mobileContent : undefined}>
        <div className={Styles.content}>
          {variant !== 'credit_balance' && (
            <div className={Styles.header}>
              <Heading level={1}>{modalHeader}</Heading>
            </div>
          )}
          <div className={Styles.top}>
            <div className={Styles.ctaAndAutoReloadContainer}>
              <div className={cx(Styles.cta, Styles[variant])}>
                <div className={Styles.creditsCount}>
                  {variant === 'no_credits' || variant === 'negative_credits' ? (
                    <AlertTriangle color="var(--red-50)" size={30} />
                  ) : (
                    <Credits color="var(--purple-50)" size={30} />
                  )}
                  <Heading level={2}>{creditsRemaining} credits</Heading>
                </div>
                {creditsExpiringSoon > 0 && (
                  <Text variant="secondary" color="var(--dark-60)">
                    {creditsExpiringSoon} credits expiring in 7 days
                  </Text>
                )}
                <div className={Styles.planInformationAndButton}>
                  {modalDescription ? (
                    <div className={cx(Styles.planInformation, { [Styles.negative]: variant === 'negative_credits' })}>
                      <span className={cx(Styles.text, Styles.messageText)}>{modalDescription}</span>
                    </div>
                  ) : (
                    <>
                      {!isOnPioneer && currentSubscription?.status === 'active' && (
                        <div className={Styles.planInformation}>
                          <span className={cx(Styles.text, Styles.messageText)}>
                            Your {currentSubscription?.plan ? nameMap[currentSubscription?.plan] : ''} plan includes{' '}
                            <strong>{baseCreditsAvailable}</strong> credits per month. Credits reset on{' '}
                            {dayjs(endDate).add(1, 'day').format('MMM D')}, and purchased credits expire after 30 days.
                          </span>
                        </div>
                      )}
                      {isOnPioneer && creditsRemaining <= 0 && (
                        <span className={cx(Styles.text, Styles.messageText)}>
                          Your Pioneer plan requires credits to be purchased to access Stylized Images and AI Videos.
                        </span>
                      )}
                    </>
                  )}
                  <div className={Styles.buttonWrapper}>
                    <Button onPress={openPurchaseCreditsModal}>{ctaButtonLabel}</Button>
                    {isTrialing && (
                      <Text variant="label">
                        to get {findPlanCreditLimit(autopilotPlans, planKey)} credits per month
                      </Text>
                    )}
                  </div>
                </div>
              </div>
              {currentSubscription?.status === 'active' && (
                <div
                  className={cx(Styles.autoReloadContainer, {
                    [Styles.on]: autoReloadIsOn,
                    [Styles.off]: autoReloadIsOff,
                    [Styles.paused]: autoReloadIsPaused,
                  })}
                >
                  <div className={Styles.iconTextAndPill}>
                    <ArrowRefresh2 />
                    <span className={Styles.text}>Auto-reload</span>
                    <div
                      className={cx(Styles.pill, {
                        [Styles.on]: autoReloadIsOn,
                        [Styles.off]: autoReloadIsOff,
                        [Styles.paused]: autoReloadIsPaused,
                      })}
                    >
                      {canAutoReload && 'ON'}
                      {workspace?.auto_reload_credits && !canAutoReload && 'PAUSED'}
                      {!workspace?.auto_reload_credits && 'OFF'}
                    </div>
                  </div>
                  <div className={Styles.manageButtonContainer}>
                    <span className={cx(Styles.text, Styles.budgetStatus)}>
                      {workspace?.auto_reload_credits &&
                        workspace?.auto_reload_limit &&
                        `$${workspace?.auto_reload_limit / 10} `}
                      {workspace?.auto_reload_credits && !workspace?.auto_reload_limit && 'Unlimited '}
                      {canAutoReload && 'monthly budget'}
                      {workspace?.auto_reload_credits && !canAutoReload && 'budget reached'}
                      {!workspace?.auto_reload_credits && 'Never run out of credits'}
                    </span>
                    <Button variant="secondary" size="lg" onPress={() => openModal(AutoReloadModal)}>
                      {workspace?.auto_reload_credits ? 'Manage' : 'Enable'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {isStarterOrFreePlan && (
              <div className={Styles.upgradeToGrowthContainer}>
                <Button frontIcon={LockOpen3} variant="tertiary" onPress={openUpgradeToGrowthModal}>
                  Upgrade to Growth to get{' '}
                  {findPlanCreditLimit(autopilotPlans, PaymentPlanKey.BlazeAutopilotGrowth).toLocaleString()} credits
                  per month
                </Button>
              </div>
            )}
            <span className={Styles.refer}>
              or{' '}
              <span className={Styles.underline} onClick={openReferralModal}>
                refer a friend
              </span>{' '}
              to get more credits
            </span>
          </div>
          <div className={Styles.bottom}>
            <div className={Styles.educationContainer}>
              <EducationRow
                text="What uses credits?"
                isOpen={openAccordion === 'what-uses-credits'}
                onToggle={() => toggleAccordion('what-uses-credits')}
              >
                {hasContentStrategies ? (
                  <div className={Styles.contentTypeGrid}>
                    {[
                      {
                        Icon: Image03,
                        label: 'Still Images',
                        cost: usageCosts?.still_image_credits_cost || 0,
                        color: '#FD4242',
                        strokeWidth: 1.15,
                      },
                      {
                        Icon: Images,
                        label: 'Carousel Posts',
                        cost: usageCosts?.carousel_credits_cost || 0,
                        color: '#EF6800',
                        strokeWidth: 1.15,
                      },
                      {
                        Icon: Iphone02,
                        label: 'Short Form Videos',
                        cost: usageCosts?.script_video_credits_cost || 0,
                        color: '#00AAFF',
                        strokeWidth: 1.15,
                      },
                      {
                        Icon: VideoOn,
                        label: 'Feed Video Posts',
                        cost: usageCosts?.animated_video_credits_cost || 0,
                        color: '#6A00FF',
                        strokeWidth: 1.15,
                      },
                      {
                        Icon: UserProfileAdd2,
                        label: 'Stories',
                        cost: usageCosts?.still_image_credits_cost || 0,
                        color: '#FF37CA',
                        strokeWidth: 1.15,
                      },
                      {
                        Icon: Email,
                        label: 'Emails',
                        cost: usageCosts?.newsletter_credits_cost || 0,
                        color: '#FFAE00',
                        strokeWidth: 1.15,
                      },
                      {
                        Icon: File02,
                        label: 'Blogs',
                        cost: usageCosts?.blog_post_credits_cost || 0,
                        color: '#20A14F',
                        strokeWidth: 1.15,
                      },
                    ].map(({ Icon, label, cost, color, strokeWidth }) => (
                      <div key={label} className={Styles.contentTypeRow}>
                        <Icon color={color} size={24} strokeWidth={strokeWidth} />
                        <div className={Styles.contentTypeInfo}>
                          <span className={Styles.contentTypeName}>
                            <Text variant="secondary">{label}</Text>
                          </span>
                          <span className={Styles.contentTypeCost}>
                            <Text variant="secondary" color="var(--dark-60)">
                              {pluralizedCredits(cost)}
                            </Text>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={Styles.whatUsesCreditsContainer}>
                    {!isOnPioneer && (
                      <span className={Styles.creditCost}>
                        {pluralizedCredits(usageCosts?.content_credits_cost || 0)} per Brand Kit and Stock Content
                      </span>
                    )}
                    <span className={Styles.creditCost}>
                      {pluralizedCredits(usageCosts?.visual_styles_credits_cost || 0)} per Stylized Image
                    </span>
                    <span className={Styles.creditCost}>
                      {pluralizedCredits(usageCosts?.ai_image_credits_cost || 0)} per AI Image
                    </span>
                    <span className={Styles.creditCost}>
                      {pluralizedCredits(usageCosts?.video_credits_cost || 0)} per AI Video
                    </span>
                  </div>
                )}
              </EducationRow>
              <EducationRow
                text="What are credits?"
                isOpen={openAccordion === 'what-are-credits'}
                onToggle={() => toggleAccordion('what-are-credits')}
              >
                <div className={Styles.whatAreCreditsContainer}>
                  <span className={Styles.whatAreCredits}>
                    Blaze uses usage-based pricing in the form of credits to allow users to create various forms of
                    content in your workspace.
                  </span>
                  <span className={Styles.whatAreCredits}>
                    This allows Blaze to integrate with the best LLM and AI tools and gives you control over the quality
                    and quantity of content you want in your marketing.
                  </span>
                  <span className={Styles.whatAreCredits}>Purchased credits expire after 90 days</span>
                </div>
              </EducationRow>
              <EducationRow
                text="How can I earn more credits?"
                isOpen={openAccordion === 'how-to-earn-credits'}
                onToggle={() => toggleAccordion('how-to-earn-credits')}
              >
                <div className={Styles.learnMoreTextContainer}>
                  <span className={Styles.learnMoreText}>
                    You can earn 500 credits for every friend or peer that you refer to Blaze using your referral link.{' '}
                    <a
                      href="https://help.blaze.ai/en/articles/13017540-blaze-credits"
                      target="_blank"
                      rel="noreferrer"
                      className={Styles.underline}
                    >
                      Learn more about Blaze's referral program
                    </a>
                    .
                  </span>
                </div>
              </EducationRow>
            </div>
            <div className={Styles.bottomButtonContainer}>
              <ButtonLink
                to="https://help.blaze.ai/en/articles/13017540-blaze-credits"
                target="_blank"
                variant="editor"
                endIcon={LinkExternal}
              >
                Learn More About Credits
              </ButtonLink>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
})
